/**
 * Rohe Jahresabschluss-Daten (insbesondere von SEC EDGAR) melden Aktienanzahl/EPS/
 * Buchwert je Aktie auf der zum Einreichungszeitpunkt gueltigen Basis - ohne rueckwirkende
 * Anpassung fuer Aktiensplits, die erst Jahre spaeter passierten (z.B. Apples 7:1-Split
 * 2014 und 4:1-Split 2020). Kombiniert mit heutigen (bereits split-bereinigten) Kursen
 * wuerde das KGV/KBV fuer alte Jahre verfaelschen, und eine reine Aktienanzahl-Zeitreihe
 * zeigt an jedem Split-Stichtag einen irrefuehrenden Sprung, der wie ein Datenfehler
 * aussieht (Erik 2026-07-29: "bei Apple ... sind hier harte Fehler drin").
 *
 * Bis 2026-07-29 loeste dieses Modul das NUR ueber eine Sprung-Heuristik
 * (findPerShareSafeStartYear): jeder Jahr-zu-Jahr-Aktienanzahl-Sprung >= 40%/<= 71,4%
 * wurde als "wahrscheinlich ein Split" gewertet und die GESAMTE aeltere Je-Aktie-Historie
 * (EPS/KGV/KBV) verworfen statt angepasst - Aktienanzahl selbst blieb dabei komplett
 * unangetastet (bewusst roh, siehe Historie in lib/valuation/multiples.ts), weil ohne
 * echtes Split-Datum/-Verhaeltnis jede Multiplikation nur eine weitere Vermutung gewesen
 * waere. Ein am 2026-07-17 gepruefter "nur runde Verhaeltnisse (2:1, 4:1, 7:1, ...) zaehlen
 * als Split"-Ansatz wurde damals bewusst verworfen, weil ein Live-Test (Citigroup
 * 2008/2009) zeigte, dass der dominante Fehlerfall gar kein Split ist, sondern
 * inkonsistente SEC-Tag-Skalierung - eine Rundungs-Heuristik haette das nicht geloest.
 *
 * Seit 2026-07-29 gibt es eine bessere Grundlage: Yahoos oeffentlicher Chart-Endpunkt
 * (`events=splits`, keine Auth noetig, live gegen AAPL [5 Splits 1987-2020] und 7203.T/
 * Toyota [5:1-Split 2021] bestaetigt) liefert echte, verifizierte Split-Stichtage samt
 * Verhaeltnis - kein Verdacht mehr, sondern ein Faktum. `cumulativeSplitFactorSince()`
 * nutzt das, um Aktienanzahl/EPS/Buchwert je Aktie fuer JEDES Jahr korrekt auf die
 * heutige Basis umzurechnen (multiplizieren bzw. dividieren), statt betroffene Jahre
 * wegzulassen - das behebt gleichzeitig den "harte Fehler"-Sprung im
 * Aktienanzahl-Zeitverlauf UND verlaengert die nutzbare EPS/KGV/KBV-Historie (Jahre vor
 * einem Split muessen nicht mehr verworfen werden, weil sie jetzt korrekt umgerechnet
 * sind), ganz ohne neue Datenquelle - nur bereits geladene Daten richtig verarbeitet.
 *
 * `findPerShareSafeStartYear()` bleibt bestehen, wechselt aber die Rolle: statt der
 * primaeren (und einzigen) Verteidigungslinie ist sie jetzt ein Sicherheitsnetz, das NUR
 * noch auf der bereits split-BEREINIGTEN Aktienanzahl-Reihe laeuft (siehe
 * providers/yahoo-provider.ts). Ein Sprung, der dort noch uebrig bleibt, ist per
 * Definition KEIN echter (jetzt bekannter) Split mehr, sondern deckt weiterhin echte
 * Datenqualitaetsfehler auf (z.B. die Citigroup- bzw. die bereits gefixte AAPL-2018/2019-
 * Tag-Merge-Inkonsistenz) - fuer genau diesen Restfall bleibt "Jahre davor weglassen" die
 * richtige, konservative Reaktion, da eine falsche Anpassung schlimmer waere als weniger
 * Historie.
 */

export interface SplitEvent {
  /** Unix-Sekunden des Split-Stichtags (Yahoo chart events.splits). */
  date: number;
  /** numerator/denominator, z.B. 4 fuer einen 4:1-Split, 0.1 fuer einen 1:10-Reverse-Split. */
  ratio: number;
}

/**
 * Kumulierter Faktor, um den ein VOR `reportDateSeconds` gemeldeter Rohwert auf die
 * heutige (nach allen seither eingetretenen Splits skalierte) Basis gebracht wird -
 * Aktienanzahl damit MULTIPLIZIEREN, EPS/Buchwert je Aktie damit DIVIDIEREN. Nur Splits
 * NACH dem Stichtag zaehlen (ein Split direkt am Stichtag selbst gilt als bereits im
 * gemeldeten Wert enthalten, siehe SEC-Fiskaljahr-Grenzfall-Kommentar in
 * providers/yahoo-provider.ts). Keine Splits (leeres Array oder Ticker ohne Split-
 * Historie) ergibt Faktor 1 - unveraendertes Verhalten, graceful degradation.
 */
export function cumulativeSplitFactorSince(
  splits: readonly SplitEvent[],
  reportDateSeconds: number,
): number {
  return splits
    .filter((s) => s.date > reportDateSeconds)
    .reduce((factor, s) => factor * s.ratio, 1);
}

/**
 * Sicherheitsnetz-Heuristik (siehe Modul-Docstring fuer die Rolle seit 2026-07-29): auf
 * einer BEREITS split-bereinigten Aktienanzahl-Reihe aufgerufen, findet sie das juengste
 * Jahr, ab dem noch ein unerklaerter Sprung (>= 40%/<= 71,4%) vorliegt - das ist dann
 * keine Split-Vermutung mehr, sondern ein Hinweis auf einen echten Datenfehler
 * (inkonsistente SEC-Tag-Skalierung o.ae.). Je-Aktie-Kennzahlen (EPS, KGV, KBV) fuer
 * Jahre davor weglassen statt mit heutigen Kursen falsch zu mischen. Umsatz/Margen/ROIC
 * sind aktienanzahl-unabhaengig und bleiben unberuehrt.
 */
export function findPerShareSafeStartYear(
  sharesByYear: Map<number, number>,
): number | null {
  const years = [...sharesByYear.keys()].sort((a, b) => a - b);
  if (years.length === 0) return null;

  let safeStartYear = years[0];
  for (let i = 1; i < years.length; i++) {
    const prevShares = sharesByYear.get(years[i - 1])!;
    const currShares = sharesByYear.get(years[i])!;
    const ratio = currShares / prevShares;
    if (ratio >= 1.4 || ratio <= 1 / 1.4) {
      safeStartYear = years[i];
    }
  }
  return safeStartYear;
}
